import styled from 'styled-components';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import Tooltip from 'components/shared/Tooltip';
import { simpleDate } from 'lib/date';
import { DenialListItem, SEX_DISPLAY } from 'models/DenialListItem';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import DenialListRowMenu from './DenialListRowMenu';

const columnHelper = createColumnHelper<DenialListItem>();

const EmptyValue = () => <>&mdash;</>;

type DenialListColumnsOptions = {
  // reasonSize is overridable so the narrower admin slideout (constrained panel width) can keep the
  // item table from scrolling horizontally; the full-width customer page keeps the default.
  reasonSize?: number;
  // rowActionsDisabled hides the per-row menu entirely (e.g. the read-only admin slideout). When
  // false, onEdit/onDelete must be supplied so the row menu can wire up the edit/delete actions.
  rowActionsDisabled?: boolean;
  onEdit?: (denialListItem: DenialListItem) => void;
  onDelete?: (denialListItem: DenialListItem) => void;
};

export default function denialListColumns({
  reasonSize = 320,
  rowActionsDisabled = false,
  onEdit,
  onDelete,
}: DenialListColumnsOptions = {}) {
  const columns: ColumnDef<DenialListItem, any>[] = [
    columnHelper.accessor('patientName', {
      id: 'patientName',
      header: 'Patient Name',
      cell: (info) => <BodySmallBold>{info.getValue() || <EmptyValue />}</BodySmallBold>,
      size: 160,
    }),
    columnHelper.accessor('dateOfBirth', {
      id: 'dateOfBirth',
      header: 'DOB',
      cell: (info) => <BodySmall>{simpleDate(info.getValue()) ?? <EmptyValue />}</BodySmall>,
      size: 110,
    }),
    columnHelper.accessor('sex', {
      id: 'sex',
      header: 'Gender',
      cell: (info) => <BodySmall>{SEX_DISPLAY[info.getValue()] ?? <EmptyValue />}</BodySmall>,
      size: 110,
    }),
    columnHelper.accessor('reason', {
      id: 'reason',
      header: 'Reason',
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <EmptyValue />;

        return (
          <Tooltip text={value} position='bottom'>
            <TruncatedReason>{value}</TruncatedReason>
          </Tooltip>
        );
      },
      size: reasonSize,
    }),
    columnHelper.accessor('dateAdded', {
      id: 'dateAdded',
      header: 'Date Added',
      cell: (info) => <BodySmall>{simpleDate(info.getValue()) ?? <EmptyValue />}</BodySmall>,
      size: 120,
      // Default-desc column: descending is the first sort direction so clicking the
      // header toggles desc -> asc instead of dropping straight to an unsorted state.
      sortDescFirst: true,
    }),
  ];

  // The per-row menu is shown only when the caller wires up edit/delete and the list isn't read-only.
  // The admin read slideout passes rowActionsDisabled (and no callbacks), so the column is omitted
  // entirely rather than rendered as a disabled placeholder. (OLIO-2461 enables it for active lists.)
  if (!rowActionsDisabled && onEdit && onDelete) {
    const handleEdit = onEdit;
    const handleDelete = onDelete;
    columns.push(
      columnHelper.display({
        id: 'menu',
        cell: (info) => (
          <DenialListRowMenu denialListItem={info.row.original} onEdit={handleEdit} onDelete={handleDelete} />
        ),
        size: 40,
        meta: { style: { justifyContent: 'flex-end' } },
      })
    );
  }

  return columns;
}

const TruncatedReason = styled(BodySmall)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${colors.black75};
`;
